import React, { Component } from "react";
import PropTypes from "prop-types";

import LineSeries from "./LineSeries";
import StraightLine from "./StraightLine";
import StaticAreaSeries from "./StaticAreaSeries";
import SVGComponent from "./SVGComponent";

class StochasticMomentumSeries extends Component {
  constructor(props) {
    super(props);
    this.yAccessorForK = this.yAccessorForK.bind(this);
    this.yAccessorForD = this.yAccessorForD.bind(this);
    this.renderClip = this.renderClip.bind(this);
    this.overBoughtClip = this.overBoughtClip.bind(this);
    this.overSoldClip = this.overSoldClip.bind(this);

    const id1 = String(Math.round(Math.random() * 10000 * 10000));
    this.clipPathId1 = `smi-clip-overbought-${id1}`;

    const id2 = String(Math.round(Math.random() * 10000 * 10000));
    this.clipPathId2 = `smi-clip-oversold-${id2}`;
  }

  overBoughtClip(ctx, moreProps) {
    const { chartConfig } = moreProps;
    const { overBought } = this.props;
    const { yScale, width } = chartConfig;

    const overBoughtValue =
      overBought?.value !== undefined ? overBought.value : 40;
    const overBoughtY = yScale(overBoughtValue);

    ctx.beginPath();
    ctx.rect(0, 0, width, overBoughtY);
    ctx.clip();
  }

  overSoldClip(ctx, moreProps) {
    const { chartConfig } = moreProps;
    const { overSold } = this.props;
    const { yScale, width, height } = chartConfig;

    const overSoldValue = overSold?.value !== undefined ? overSold.value : -40;
    const overSoldY = yScale(overSoldValue);

    ctx.beginPath();
    ctx.rect(0, overSoldY, width, height - overSoldY);
    ctx.clip();
  }

  renderClip(moreProps) {
    const { chartConfig } = moreProps;
    const { overBought, overSold } = this.props;
    const { yScale, width, height } = chartConfig;

    const overBoughtValue =
      overBought?.value !== undefined ? overBought.value : 40;
    const overSoldValue = overSold?.value !== undefined ? overSold.value : -40;

    const overBoughtY = yScale(overBoughtValue);
    const overSoldY = yScale(overSoldValue);

    return (
      <defs>
        <clipPath id={this.clipPathId1}>
          <rect x={0} y={0} width={width} height={overBoughtY} />
        </clipPath>
        <clipPath id={this.clipPathId2}>
          <rect x={0} y={overSoldY} width={width} height={height - overSoldY} />
        </clipPath>
      </defs>
    );
  }

  static yExtents(datum, index, allData, indicator) {
    const dataArr = Array.isArray(allData)
      ? allData
      : Array.isArray(datum)
        ? datum
        : [datum];

    if (!indicator || !indicator.renderKey) {
      return [-60, 60];
    }

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    dataArr.forEach((item) => {
      const node = item && item[indicator.renderKey];
      if (!node || typeof node !== "object") return;

      const updateExtents = (value) => {
        if (!Number.isFinite(value)) return;
        if (value < min) min = value;
        if (value > max) max = value;
      };

      updateExtents(node.k);
      updateExtents(node.d);
    });

    // Default to -60 to 60 if no valid data
    if (
      !Number.isFinite(min) ||
      !Number.isFinite(max) ||
      min === Number.POSITIVE_INFINITY
    ) {
      return [-60, 60];
    }

    // Ensure overbought/oversold zones are visible
    min = Math.min(min, -40);
    max = Math.max(max, 40);

    // Add padding (10% of range)
    const range = max - min;
    const padding = range * 0.1;

    return [min - padding, max + padding];
  }

  yAccessorForK(d) {
    const { yAccessor } = this.props;
    return yAccessor(d) && yAccessor(d).k;
  }

  yAccessorForD(d) {
    const { yAccessor } = this.props;
    return yAccessor(d) && yAccessor(d).d;
  }

  render() {
    const { className, onDoubleClick, onContextMenu } = this.props;
    const { strokeK, strokeD, showZones, overBought, overSold, zeroLine } =
      this.props;
    const { clip } = this.props;

    const overBoughtValue =
      overBought?.value !== undefined ? overBought.value : 40;
    const overSoldValue = overSold?.value !== undefined ? overSold.value : -40;
    const overBoughtColor = overBought?.stroke?.color || "#B8C2CC";
    const overSoldColor = overSold?.stroke?.color || "#B8C2CC";

    const style1 = { clipPath: `url(#${this.clipPathId1})` };
    const style2 = { clipPath: `url(#${this.clipPathId2})` };

    return (
      <g className={className}>
        <SVGComponent>{this.renderClip}</SVGComponent>

        {/* Overbought/Oversold Zone Fills - Fill between %K and zone lines */}
        {showZones && (
          <>
            {/* Overbought Zone Fill - Only shows when %K > overbought */}
            <StaticAreaSeries
              style={style1}
              canvasClip={this.overBoughtClip}
              yAccessor={this.yAccessorForK}
              base={(yScale) => yScale(overBoughtValue)}
              fill={overBoughtColor}
              opacity={0.2}
            />

            {/* Oversold Zone Fill - Only shows when %K < oversold */}
            <StaticAreaSeries
              style={style2}
              canvasClip={this.overSoldClip}
              yAccessor={this.yAccessorForK}
              base={(yScale) => yScale(overSoldValue)}
              fill={overSoldColor}
              opacity={0.2}
            />
          </>
        )}

        {/* Main %K Line */}
        <LineSeries
          yAccessor={this.yAccessorForK}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={strokeK}
          clip={clip}
        />

        {/* %D Signal Line */}
        <LineSeries
          yAccessor={this.yAccessorForD}
          highlightOnHover
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          stroke={strokeD}
          clip={clip}
        />

        {/* Zero Line */}
        {zeroLine && (
          <StraightLine
            highlightOnHover
            onDoubleClick={onDoubleClick}
            onContextMenu={onContextMenu}
            stroke={
              zeroLine?.stroke || {
                color: "#8795A1",
                dash: "Solid",
                width: 1,
                opacity: 1,
              }
            }
            yValue={0}
          />
        )}

        {/* Overbought/Oversold Lines */}
        {showZones && (
          <>
            <StraightLine
              highlightOnHover
              onDoubleClick={onDoubleClick}
              onContextMenu={onContextMenu}
              stroke={
                overBought?.stroke || {
                  color: "#B8C2CC",
                  dash: "Dash",
                  width: 1,
                  opacity: 1,
                }
              }
              yValue={overBoughtValue}
            />
            <StraightLine
              highlightOnHover
              onDoubleClick={onDoubleClick}
              onContextMenu={onContextMenu}
              stroke={
                overSold?.stroke || {
                  color: "#B8C2CC",
                  dash: "Dash",
                  width: 1,
                  opacity: 1,
                }
              }
              yValue={overSoldValue}
            />
          </>
        )}
      </g>
    );
  }
}

StochasticMomentumSeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  clip: PropTypes.bool.isRequired,
  strokeK: PropTypes.object.isRequired,
  strokeD: PropTypes.object.isRequired,
  showZones: PropTypes.bool,
  overBought: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      value: PropTypes.number,
      stroke: PropTypes.object,
    }),
  ]),
  overSold: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      value: PropTypes.number,
      stroke: PropTypes.object,
    }),
  ]),
  zeroLine: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      stroke: PropTypes.object,
    }),
  ]),
  onDoubleClick: PropTypes.func,
  onContextMenu: PropTypes.func,
};

StochasticMomentumSeries.defaultProps = {
  className: "react-stockcharts-stochastic-momentum-series",
  clip: true,
  showZones: true,
  overBought: 40,
  overSold: -40,
  zeroLine: {
    stroke: { color: "#8795A1", dash: "Solid", width: 1, opacity: 1 },
  },
};

export default StochasticMomentumSeries;
